import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export type AchievementType =
  | 'beta_survivor'
  | 'og_founder'
  | 'first_purchase'
  | 'first_flip'
  | 'first_royalty'
  | 'first_post'
  | 'first_message'
  | 'first_dm_spotlight'
  | 'first_post_spotlight'
  | 'first_profile_spotlight'
  | 'first_megaphone'
  | 'spotlight_dm_received_10'
  | 'spotlight_dm_received_100'
  | 'spotlight_dm_received_1000'
  | 'spotlight_post_received_10'
  | 'spotlight_post_received_100'
  | 'spotlight_post_received_1000'
  | 'spotlight_profile_received_10'
  | 'spotlight_profile_received_100'
  | 'spotlight_profile_received_1000';

export type AchievementCategory =
  | 'early_access'
  | 'firsts'
  | 'reputation'
  | 'spending'
  | 'spotlight_received'
  | 'spotlight_streak';

export interface AchievementCategoryEntry {
  id: AchievementCategory;
  label: string;
  blurb: string;
}

export interface Achievement {
  type: AchievementType;
  name: string;
  icon: string;
  description: string;
  category: AchievementCategory;
  awardedAt: string;
  txHash: string | null;
  featuredOrder: number | null;
}

export interface EarnedAchievement extends Achievement {
  rarityPct: number;
  holders: number;
}

export interface LockedAchievement {
  type: AchievementType;
  name: string;
  icon: string;
  description: string;
  howTo: string;
  category: AchievementCategory;
  rarityPct: number;
  holders: number;
}

export interface AchievementsFullPayload {
  earned: EarnedAchievement[];
  locked: LockedAchievement[];
  categories: AchievementCategoryEntry[];
  stats: { unlocked: number; total: number; totalUsers: number };
  user: { username: string | null; displayName: string | null };
}

export type EarnPayoutType =
  | 'fixed_klv'
  | 'pct_of_fee_from_trigger_tx'
  | 'pct_of_accumulated_fees';

export interface EarnConfigRow {
  achievementType: string;
  payoutType: EarnPayoutType;
  payoutValue: number;
}

export interface EarnConfigPayload {
  multiplier: number;
  rows: EarnConfigRow[];
}

const stripAt = (username: string) =>
  username.startsWith('@') ? username.slice(1) : username;

export const fetchAchievements = createAsyncThunk(
  'achievement/fetchByUsername',
  async (username: string, { rejectWithValue }) => {
    try {
      const clean = stripAt(username);
      const response = await api.get<{ achievements: Achievement[] }>(
        `/user-profile/${clean}/achievements`
      );
      return { username: clean, achievements: response.data.achievements };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to fetch achievements');
    }
  }
);

export const fetchAllAchievements = createAsyncThunk(
  'achievement/fetchAllByUsername',
  async (username: string, { rejectWithValue }) => {
    try {
      const clean = stripAt(username);
      const response = await api.get<AchievementsFullPayload>(
        `/user-profile/${clean}/achievements/all`
      );
      return { username: clean, payload: response.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to fetch achievements');
    }
  }
);

export const fetchEarnConfig = createAsyncThunk<EarnConfigPayload>(
  'achievement/fetchEarnConfig',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get<EarnConfigPayload>('/achievements/earn-config');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to load earn config');
    }
  },
);

export const updateFeaturedAchievements = createAsyncThunk(
  'achievement/updateFeatured',
  async (
    arg: { username: string; featured: AchievementType[] },
    { rejectWithValue },
  ) => {
    try {
      await api.put('/user-profile/me/achievements/featured', { featured: arg.featured });
      // Returning username + featured lets the slice patch byUsername /
      // fullByUsername for the caller in the fulfilled reducer, so the
      // profile sidebar reflects the new pin order without waiting for
      // a re-fetch on the next mount.
      return { username: stripAt(arg.username), featured: arg.featured };
    } catch (err: any) {
      if (err.response?.status === 401) {
        return rejectWithValue('Session expired — please sign in again to pin achievements.');
      }
      return rejectWithValue(err.response?.data?.error ?? 'Failed to update featured achievements');
    }
  }
);
